import React, { useState } from "react";
import {
  Modal,
  List,
  Button,
  Tag,
  Typography,
  Divider,
  message,
  Select,
  Input,
  Tooltip,
} from "antd";
import styled from "styled-components";
import {
  DollarOutlined,
  CalendarOutlined,
  UserOutlined,
  HomeOutlined,
  FileTextOutlined,
  RiseOutlined,
  BankOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { trackEvent } from "../helpers/mixpanel";
import { getUnitDisplayAddress } from "../lib/unitAddressUtils";
import moment from "moment";

const { Title, Text } = Typography;
const { Option } = Select;

const OwnerStatementsModal = ({
  isOpen,
  onClose,
  tenants = [],
  units = [],
  payments = [],
  totalRent = 0,
  collectedRent = 0,
  onGenerateStatement,
}) => {
  const [selectedDateRange, setSelectedDateRange] = React.useState([
    moment().subtract(1, "month").startOf("month"), // Start: beginning of last month
    moment().endOf("month"), // End: end of current month
  ]);
  const [isGenerating, setIsGenerating] = useState(false);

  const currentYear = moment().year();
  const yearOptions = React.useMemo(
    () => Array.from({ length: 60 }, (_, i) => currentYear - 40 + i),
    [currentYear]
  );

  const handleGenerateStatement = async () => {
    // Always generate PDF - onGenerateStatement prop is for custom handling if needed
    await generateOwnerStatement();

    // Also call the prop if provided (for tracking/analytics)
    if (onGenerateStatement) {
      try {
        onGenerateStatement();
      } catch (error) {
        console.warn("Error in onGenerateStatement callback:", error);
      }
    }
  };

  const generateOwnerStatement = async () => {
    console.log("[OwnerStatementsModal] Starting PDF generation...");
    setIsGenerating(true);
    try {
      const startDate = selectedDateRange[0];
      const endDate = selectedDateRange[1];

      const safePayments = payments || [];
      const safeTenants = tenants || [];
      const safeUnits = units || [];

      console.log(
        "[OwnerStatementsModal] Date range:",
        startDate.format("YYYY-MM-DD"),
        "to",
        endDate.format("YYYY-MM-DD")
      );
      console.log(
        "[OwnerStatementsModal] Total payments:",
        safePayments.length
      );

      // Filter payments for the selected period
      const periodPayments = safePayments.filter((payment) => {
        // Extract payment date - handle multiple possible formats
        // Priority: paidDate > dueDate > createdAt
        let paymentDate = null;

        // Try paidDate first
        if (payment.paidDate) {
          if (payment.paidDate.toDate) {
            paymentDate = payment.paidDate.toDate();
          } else if (payment.paidDate instanceof Date) {
            paymentDate = payment.paidDate;
          } else {
            paymentDate = new Date(payment.paidDate);
          }
        }
        // Fallback to dueDate if paidDate doesn't exist
        else if (payment.dueDate) {
          if (payment.dueDate.toDate) {
            paymentDate = payment.dueDate.toDate();
          } else if (payment.dueDate instanceof Date) {
            paymentDate = payment.dueDate;
          } else {
            paymentDate = new Date(payment.dueDate);
          }
        }
        // Fallback to createdAt
        else if (payment.createdAt) {
          if (payment.createdAt.toDate) {
            paymentDate = payment.createdAt.toDate();
          } else if (payment.createdAt instanceof Date) {
            paymentDate = payment.createdAt;
          } else {
            paymentDate = new Date(payment.createdAt);
          }
        }

        // If no valid date found, skip this payment
        if (!paymentDate || isNaN(paymentDate.getTime())) {
          return false;
        }

        // Get date range boundaries (start of start date, end of end date)
        const startDateOnly = startDate.startOf("day").toDate();
        const endDateOnly = endDate.endOf("day").toDate();

        // Compare dates (normalize payment date to start of day for comparison)
        const paymentDateOnly = new Date(
          paymentDate.getFullYear(),
          paymentDate.getMonth(),
          paymentDate.getDate()
        );
        const startDateNormalized = new Date(
          startDateOnly.getFullYear(),
          startDateOnly.getMonth(),
          startDateOnly.getDate()
        );
        const endDateNormalized = new Date(
          endDateOnly.getFullYear(),
          endDateOnly.getMonth(),
          endDateOnly.getDate()
        );

        return (
          paymentDateOnly >= startDateNormalized &&
          paymentDateOnly <= endDateNormalized
        );
      });

      // Calculate financial metrics for the period
      const totalRentCollected = periodPayments
        .filter((p) => {
          const s = (p?.status || "").toLowerCase();
          return s === "paid" || s === "succeeded";
        })
        .reduce((sum, p) => sum + (p.amount || 0), 0);

      const totalRentDue = periodPayments.reduce(
        (sum, p) => sum + (p.amount || 0),
        0
      );
      const collectionRate =
        totalRentDue > 0
          ? Math.round((totalRentCollected / totalRentDue) * 100)
          : 0;

      const activeTenants = safeTenants.filter(
        (t) => t.leaseStatus === "Active"
      ).length;
      const occupiedUnits = safeUnits.filter((unit) =>
        safeTenants.some(
          (tenant) =>
            tenant.unitId === unit.id && tenant.leaseStatus === "Active"
        )
      ).length;

      const averageRent =
        activeTenants > 0 ? Math.round(totalRent / activeTenants) : 0;

      // Calculate expenses (placeholder - would come from expenses model)
      const estimatedExpenses = Math.round(totalRentCollected * 0.15); // 15% of rent for maintenance, management, etc.
      const netIncome = totalRentCollected - estimatedExpenses;

      // Create comprehensive owner statement
      const statementData = {
        title: "Owner Financial Statement",
        period: `${startDate.format("MMMM YYYY")}`,
        generatedDate: new Date().toLocaleDateString(),
        ownerName: "Property Owner", // Would come from user profile
        totalRentCollected,
        totalRentDue,
        collectionRate,
        activeTenants,
        occupiedUnits,
        totalUnits: safeUnits.length,
        averageRent,
        estimatedExpenses,
        netIncome,
        occupancyRate:
          safeUnits.length > 0
            ? Math.round((occupiedUnits / safeUnits.length) * 100)
            : 0,
        payments: periodPayments.map((payment) => {
          const tenant = safeTenants.find((t) => t.id === payment.tenantId);
          const unit = safeUnits.find((u) => u.id === payment.unitId);
          return {
            id: payment.id,
            tenantName: tenant
              ? `${tenant.firstName || ""} ${tenant.lastName || ""}`.trim() ||
                tenant.name
              : "Unknown Tenant",
            unitName: unit
              ? unit.name || getUnitDisplayAddress(unit)
              : "Unknown door",
            amount: payment.amount || 0,
            status: payment.status || "Unknown",
            paidDate:
              payment.paidDate?.toDate?.()?.toLocaleDateString() ||
              payment.createdAt?.toDate?.()?.toLocaleDateString() ||
              "Unknown",
            dueDate:
              payment.dueDate?.toDate?.()?.toLocaleDateString() || "Unknown",
          };
        }),
        units: safeUnits.map((unit) => {
          const tenant = safeTenants.find(
            (t) => t.unitId === unit.id && t.leaseStatus === "Active"
          );
          return {
            id: unit.id,
            name: unit.name || getUnitDisplayAddress(unit),
            monthlyRent:
              unit.monthlyRent || unit.currentRent || unit.baseRent || 0,
            status: tenant ? "Occupied" : "Vacant",
            tenantName: tenant
              ? `${tenant.firstName || ""} ${tenant.lastName || ""}`.trim() ||
                tenant.name
              : "Vacant",
          };
        }),
      };

      console.log("[OwnerStatementsModal] Statement data prepared:", {
        period: statementData.period,
        totalRentCollected,
        totalRentDue,
        paymentsCount: periodPayments.length,
      });

      // Generate PDF content
      const pdfContent = generatePDFContent(statementData);
      console.log(
        "[OwnerStatementsModal] PDF content generated, length:",
        pdfContent.length
      );

      // Create and download the PDF
      console.log("[OwnerStatementsModal] Starting PDF download...");
      await downloadPDF(
        pdfContent,
        `owner-statement-${statementData.period
          .replace(" ", "-")
          .toLowerCase()}.pdf`
      );
      console.log(
        "[OwnerStatementsModal] PDF generation completed successfully"
      );

      // Track the event
      try {
        trackEvent("owner_statement_generated", {
          period: statementData.period,
          totalRentCollected,
          collectionRate,
          activeTenants,
          occupiedUnits,
          statementType: "owner_financial_statement",
        });
      } catch (error) {
        console.warn("Failed to track owner statement generation:", error);
      }
    } catch (error) {
      console.error("Error generating owner statement:", error);
      message.error("Failed to generate owner statement. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const generatePDFContent = (data) => {
    const htmlContent = `
 <!DOCTYPE html>
 <html>
 <head>
 <meta charset="utf-8">
 <title>${data.title}</title>
 <style>
 * {
 margin: 0;
 padding: 0;
 box-sizing: border-box;
 }
 body {
 font-family: 'Times New Roman', Times, serif;
 font-size: 11pt;
 line-height: 1.4;
 color: #333333;
 background-color: #ffffff;
 }
 .report-container {
 max-width: 8.5in;
 margin: 0 auto;
 padding: 0.75in;
 background-color: #ffffff;
 }
 .report-header {
 text-align: center;
 margin-bottom: 30px;
 padding-bottom: 15px;
 border-bottom: 3px solid #2c5f2d;
 }
 .report-title {
 font-size: 24pt;
 font-weight: bold;
 color: #2c5f2d;
 margin-bottom: 10px;
 text-transform: uppercase;
 letter-spacing: 1px;
 }
 .report-meta {
 font-size: 9pt;
 color: #666666;
 margin-top: 8px;
 }
 .report-meta div {
 margin: 3px 0;
 }
 .executive-summary {
 margin-bottom: 30px;
 page-break-inside: avoid;
 }
 .section-title {
 font-size: 14pt;
 font-weight: bold;
 color: #2c5f2d;
 margin-bottom: 15px;
 text-transform: uppercase;
 letter-spacing: 0.5px;
 border-bottom: 2px solid #2c5f2d;
 padding-bottom: 5px;
 }
 .summary-table {
 width: 100%;
 border-collapse: collapse;
 margin-bottom: 20px;
 font-size: 10pt;
 }
 .summary-table td {
 padding: 12px 15px;
 border: 1px solid #cccccc;
 font-size: 10pt;
 }
 .summary-table td:first-child {
 font-weight: bold;
 background-color: #f5f5f5;
 width: 40%;
 color: #333333;
 }
 .summary-table td:last-child {
 text-align: right;
 font-weight: bold;
 color: #2c5f2d;
 font-size: 11pt;
 }
 .payments-section {
 margin-top: 20rem;
 padding-top: 0;
 page-break-inside: avoid;
 page-break-before: always;
 }
 .payments-section .section-title {
 margin-top: 0;
 margin-bottom: 20px;
 }
 .payments-table {
 width: 100%;
 border-collapse: collapse;
 margin-top: 15px;
 font-size: 9pt;
 table-layout: fixed;
 word-wrap: break-word;
 border-spacing: 0;
 }
 .payments-table thead {
 background-color: #ffffff;
 border-bottom: 2px solid #2c5f2d;
 }
 .payments-table th {
 padding: 10px 8px;
 text-align: left;
 font-weight: bold;
 font-size: 8pt;
 text-transform: uppercase;
 letter-spacing: 0.5px;
 color: #2c5f2d;
 border: 1px solid #2c5f2d;
 background-color: #ffffff;
 white-space: nowrap;
 overflow: hidden;
 text-overflow: ellipsis;
 }
 .payments-table th:nth-child(1) { width: 20%; } /* Tenant */
 .payments-table th:nth-child(2) { width: 15%; } /* Unit */
 .payments-table th:nth-child(3) { width: 12%; } /* Amount */
 .payments-table th:nth-child(4) { width: 12%; } /* Status */
 .payments-table th:nth-child(5) { width: 15%; } /* Paid Date */
 .payments-table th:nth-child(6) { width: 15%; } /* Due Date */
 .payments-table th:nth-child(3),
 .payments-table th:nth-child(4),
 .payments-table th:nth-child(5),
 .payments-table th:nth-child(6) {
 text-align: center;
 }
 .payments-table th:nth-child(3) {
 text-align: right;
 }
 .payments-table td {
 padding: 8px 8px;
 border: 1px solid #cccccc;
 vertical-align: top;
 font-size: 9pt;
 white-space: normal;
 word-wrap: break-word;
 overflow-wrap: break-word;
 line-height: 1.4;
 overflow-x: hidden;
 overflow-y: visible;
 min-height: 1.4em;
 }
 .payments-table td:nth-child(1) {
 word-break: break-word;
 }
 .payments-table td:nth-child(3) {
 text-align: right;
 font-weight: bold;
 color: #2c5f2d;
 white-space: nowrap;
 }
 .payments-table td:nth-child(4),
 .payments-table td:nth-child(5),
 .payments-table td:nth-child(6) {
 text-align: center;
 font-size: 8pt;
 white-space: nowrap;
 overflow-x: hidden;
 overflow-y: visible;
 }
 .payments-table tbody tr {
 height: auto;
 min-height: 2em;
 }
 .payments-table tbody tr:nth-child(even) {
 background-color: #f9f9f9;
 }
 .status-paid {
 color: #155724;
 font-weight: bold;
 font-size: 8pt;
 }
 .status-pending {
 color: #856404;
 font-weight: bold;
 font-size: 8pt;
 }
 .status-overdue {
 color: #721c24;
 font-weight: bold;
 font-size: 8pt;
 }
 .status-unknown {
 color: #666666;
 font-weight: bold;
 font-size: 8pt;
 }
 .units-section {
 margin-top: 30px;
 padding-top: 0;
 page-break-inside: avoid;
 page-break-before: avoid;
 }
 .units-section .section-title {
 margin-top: 0;
 margin-bottom: 20px;
 }
 .units-table {
 width: 100%;
 border-collapse: collapse;
 margin-top: 15px;
 font-size: 9pt;
 table-layout: fixed;
 word-wrap: break-word;
 border-spacing: 0;
 }
 .units-table thead {
 background-color: #ffffff;
 border-bottom: 2px solid #2c5f2d;
 }
 .units-table th {
 padding: 10px 8px;
 text-align: left;
 font-weight: bold;
 font-size: 8pt;
 text-transform: uppercase;
 letter-spacing: 0.5px;
 color: #2c5f2d;
 border: 1px solid #2c5f2d;
 background-color: #ffffff;
 white-space: nowrap;
 overflow: hidden;
 text-overflow: ellipsis;
 }
 .units-table th:nth-child(1) { width: 30%; } /* Unit Name */
 .units-table th:nth-child(2) { width: 20%; } /* Tenant */
 .units-table th:nth-child(3) { width: 15%; } /* Status */
 .units-table th:nth-child(4) { width: 20%; } /* Monthly Rent */
 .units-table th:nth-child(3) {
 text-align: center;
 }
 .units-table th:nth-child(4) {
 text-align: right;
 }
 .units-table td {
 padding: 8px 8px;
 border: 1px solid #cccccc;
 vertical-align: top;
 font-size: 9pt;
 white-space: normal;
 word-wrap: break-word;
 overflow-wrap: break-word;
 line-height: 1.4;
 overflow-x: hidden;
 overflow-y: visible;
 min-height: 1.4em;
 }
 .units-table td:nth-child(3) {
 text-align: center;
 font-size: 8pt;
 }
 .units-table td:nth-child(4) {
 text-align: right;
 font-weight: bold;
 color: #2c5f2d;
 white-space: nowrap;
 }
 .units-table tbody tr {
 height: auto;
 min-height: 2em;
 }
 .units-table tbody tr:nth-child(even) {
 background-color: #f9f9f9;
 }
 .disclaimer {
 margin-top: 30px;
 padding: 15px;
 background-color: #f8f9fa;
 border-left: 4px solid #2c5f2d;
 font-size: 9pt;
 color: #333333;
 line-height: 1.5;
 page-break-inside: avoid;
 }
 .disclaimer-title {
 font-weight: bold;
 margin-bottom: 5px;
 color: #2c5f2d;
 }
 .footer-section {
 margin-top: 40px;
 padding-top: 20px;
 border-top: 1px solid #cccccc;
 font-size: 9pt;
 color: #666666;
 text-align: center;
 page-break-inside: avoid;
 }
 .page-break {
 page-break-before: always;
 }
 @media print {
 .report-container {
 padding: 0.5in;
 }
 .payments-table tbody tr,
 .units-table tbody tr {
 page-break-inside: avoid;
 }
 .payments-section,
 .units-section {
 page-break-after: avoid;
 }
 .disclaimer {
 page-break-before: avoid;
 }
 }
 </style>
 </head>
 <body>
 <div class="report-container">
 <div class="report-header">
 <div class="report-title">${data.title}</div>
 <div class="report-meta">
 <div>Period: ${data.period}</div>
 <div>Report Date: ${data.generatedDate}</div>
 <div>Generated by: Leo AI Assistant - LandlordOps</div>
 </div>
 </div>

 <div class="executive-summary">
 <div class="section-title">Executive Summary</div>
 
 <div style="margin-bottom: 25px;">
 <div style="font-size: 11pt; font-weight: bold; color: #2c5f2d; margin-bottom: 15px; text-transform: uppercase; letter-spacing: 0.5px;">Portfolio Overview</div>
 <table class="summary-table">
 <tr>
  <td>Total Doors</td>
  <td>${data.totalUnits}</td>
 </tr>
 <tr>
  <td>Occupied Doors</td>
  <td>${data.occupiedUnits}</td>
 </tr>
 <tr>
  <td>Active Tenants</td>
  <td>${data.activeTenants}</td>
 </tr>
 <tr>
  <td>Occupancy Rate</td>
  <td>${data.occupancyRate}%</td>
 </tr>
 </table>
 </div>

 <div style="margin-bottom: 25px;">
 <div style="font-size: 11pt; font-weight: bold; color: #2c5f2d; margin-bottom: 15px; text-transform: uppercase; letter-spacing: 0.5px;">Financial Metrics - ${
   data.period
 }</div>
 <table class="summary-table">
 <tr>
  <td>Total Rent Collected</td>
  <td>$${data.totalRentCollected.toLocaleString()}</td>
 </tr>
 <tr>
  <td>Total Rent Due</td>
  <td>$${data.totalRentDue.toLocaleString()}</td>
 </tr>
 <tr>
  <td>Collection Rate</td>
  <td>${data.collectionRate}%</td>
 </tr>
 <tr>
  <td>Estimated Expenses</td>
  <td>$${data.estimatedExpenses.toLocaleString()}</td>
 </tr>
 <tr>
  <td>Net Income</td>
  <td>$${data.netIncome.toLocaleString()}</td>
 </tr>
 ${
   data.averageRent
     ? `
 <tr>
  <td>Average Monthly Rent</td>
  <td>$${data.averageRent.toLocaleString()}</td>
 </tr>
 `
     : ""
 }
 </table>
 </div>
 </div>

 ${
   data.payments && data.payments.length > 0
     ? `
 <div class="payments-section">
 <div class="section-title">Payment Details</div>
 <table class="payments-table">
 <thead>
 <tr>
  <th>Tenant</th>
  <th>Unit</th>
  <th>Amount</th>
  <th>Status</th>
  <th>Paid Date</th>
  <th>Due Date</th>
 </tr>
 </thead>
 <tbody>
 ${data.payments
   .map(
     (payment) => `
  <tr>
  <td><strong>${payment.tenantName || "Unknown Tenant"}</strong></td>
  <td>${payment.unitName || "Unknown Door"}</td>
  <td>$${payment.amount.toLocaleString()}</td>
  <td><span class="status-${(payment.status || "unknown").toLowerCase()}">${
    payment.status || "Unknown"
  }</span></td>
  <td>${payment.paidDate || "N/A"}</td>
  <td>${payment.dueDate || "N/A"}</td>
  </tr>
 `
   )
   .join("")}
 </tbody>
 </table>
 </div>
 `
     : ""
 }

 ${
   data.units && data.units.length > 0
     ? `
 <div class="units-section">
 <div class="section-title">Unit Details</div>
 <table class="units-table">
 <thead>
 <tr>
  <th>Unit Name</th>
  <th>Tenant</th>
  <th>Status</th>
  <th>Monthly Rent</th>
 </tr>
 </thead>
 <tbody>
 ${data.units
   .map(
     (unit) => `
  <tr>
  <td><strong>${unit.name || "Unknown Door"}</strong></td>
  <td>${unit.tenantName || "Vacant"}</td>
  <td>${unit.status || "Unknown"}</td>
  <td>$${unit.monthlyRent.toLocaleString()}</td>
  </tr>
 `
   )
   .join("")}
 </tbody>
 </table>
 </div>
 `
     : ""
 }

 <div class="disclaimer">
 <div class="disclaimer-title">Report Information</div>
 <div>
 This financial statement provides a comprehensive overview of rental income, expenses, and property performance 
 for the period ${data.period}. All amounts listed are accurate as of ${
   data.generatedDate
 }. 
 This document may be used for tax purposes, financial planning, and investment analysis.
 For questions regarding this statement, please contact your property management team.
 </div>
 </div>

 <div class="footer-section">
 <div>This document was generated by LandlordOps</div>
 <div style="margin-top: 5px;">© ${new Date().getFullYear()} LandlordOps. All rights reserved.</div>
 </div>
 </div>
 </body>
 </html>
 `;

    return htmlContent;
  };

  const downloadPDF = async (htmlContent, filename) => {
    try {
      console.log(
        "[OwnerStatementsModal] downloadPDF called with filename:",
        filename
      );
      // Create a temporary container element with proper styling
      const tempDiv = document.createElement("div");
      tempDiv.style.position = "absolute";
      tempDiv.style.left = "-9999px";
      tempDiv.style.top = "0";
      tempDiv.style.width = "210mm"; // A4 width
      tempDiv.style.backgroundColor = "#ffffff";
      tempDiv.style.padding = "0";
      tempDiv.style.margin = "0";
      tempDiv.innerHTML = htmlContent;
      document.body.appendChild(tempDiv);

      // Wait a bit for styles to apply
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Wait for images to load
      await new Promise((resolve) => {
        const images = tempDiv.getElementsByTagName("img");
        let loadedImages = 0;
        const totalImages = images.length;

        if (totalImages === 0) {
          resolve();
          return;
        }

        const imageLoaded = () => {
          loadedImages++;
          if (loadedImages === totalImages) {
            resolve();
          }
        };

        Array.from(images).forEach((img) => {
          if (img.complete) {
            imageLoaded();
          } else {
            img.onload = imageLoaded;
            img.onerror = imageLoaded;
          }
        });
      });

      // Convert HTML to canvas with optimized settings for smaller file size
      const canvas = await html2canvas(tempDiv, {
        scale: 1.5, // Reduced from 2 to 1.5 for smaller file size (still good quality)
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        width: tempDiv.scrollWidth,
        height: tempDiv.scrollHeight,
        windowWidth: tempDiv.scrollWidth,
        windowHeight: tempDiv.scrollHeight,
        allowTaint: false,
        foreignObjectRendering: false,
      });

      // Remove temporary element
      document.body.removeChild(tempDiv);

      // Calculate PDF dimensions
      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const pdf = new jsPDF("p", "mm", "a4");

      // Add pages if content is taller than one page
      const pageHeight = 297; // A4 height in mm
      const pageWidth = 210; // A4 width in mm
      let heightLeft = imgHeight;
      let position = 0;

      // Use JPEG format with compression for much smaller file size
      // Quality 0.85 provides good balance between quality and file size
      const imageData = canvas.toDataURL("image/jpeg", 0.85);
      // Add first page
      pdf.addImage(imageData, "JPEG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imageData, "JPEG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Download the PDF
      console.log("[OwnerStatementsModal] Saving PDF file:", filename);
      pdf.save(filename);
      console.log("[OwnerStatementsModal] PDF saved successfully");

      // Show success message
      message.success("Owner statement generated and downloaded successfully!");
    } catch (error) {
      console.error("[OwnerStatementsModal] Error generating PDF:", error);
      console.error("[OwnerStatementsModal] Error stack:", error.stack);
      message.error("Failed to generate PDF. Please try again.");
      throw error;
    }
  };

  const getStatusColor = (status) => {
    const s = (status || "").toLowerCase();
    // Stripe and other gateways use "succeeded" - must not fall through to `default` (poor contrast in theme)
    if (["paid", "succeeded", "success", "completed", "complete"].includes(s))
      return "success";
    if (["pending", "processing"].includes(s)) return "warning";
    if (["overdue", "failed", "error", "canceled", "cancelled"].includes(s))
      return "error";
    return "default";
  };

  // Calculate current period metrics (defensive: ensure arrays)
  const currentPeriodPayments = (payments || []).filter((payment) => {
    // Extract payment date - handle multiple possible formats
    // Priority: paidDate > dueDate > createdAt
    let paymentDate = null;

    // Try paidDate first
    if (payment.paidDate) {
      if (payment.paidDate.toDate) {
        paymentDate = payment.paidDate.toDate();
      } else if (payment.paidDate instanceof Date) {
        paymentDate = payment.paidDate;
      } else {
        paymentDate = new Date(payment.paidDate);
      }
    }
    // Fallback to dueDate if paidDate doesn't exist
    else if (payment.dueDate) {
      if (payment.dueDate.toDate) {
        paymentDate = payment.dueDate.toDate();
      } else if (payment.dueDate instanceof Date) {
        paymentDate = payment.dueDate;
      } else {
        paymentDate = new Date(payment.dueDate);
      }
    }
    // Fallback to createdAt
    else if (payment.createdAt) {
      if (payment.createdAt.toDate) {
        paymentDate = payment.createdAt.toDate();
      } else if (payment.createdAt instanceof Date) {
        paymentDate = payment.createdAt;
      } else {
        paymentDate = new Date(payment.createdAt);
      }
    }

    // If no valid date found, skip this payment
    if (!paymentDate || isNaN(paymentDate.getTime())) {
      return false;
    }

    // Get date range boundaries (start of start date, end of end date)
    const startDate = selectedDateRange[0].startOf("day").toDate();
    const endDate = selectedDateRange[1].endOf("day").toDate();

    // Compare dates (normalize payment date to start of day for comparison)
    const paymentDateOnly = new Date(
      paymentDate.getFullYear(),
      paymentDate.getMonth(),
      paymentDate.getDate()
    );
    const startDateOnly = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate()
    );
    const endDateOnly = new Date(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate()
    );

    return paymentDateOnly >= startDateOnly && paymentDateOnly <= endDateOnly;
  });

  const totalRentCollected = currentPeriodPayments
    .filter((p) => {
      const s = (p?.status || "").toLowerCase();
      return s === "paid" || s === "succeeded";
    })
    .reduce((sum, p) => sum + (p.amount || 0), 0);

  const totalRentDue = currentPeriodPayments.reduce(
    (sum, p) => sum + (p.amount || 0),
    0
  );
  const collectionRate =
    totalRentDue > 0
      ? Math.round((totalRentCollected / totalRentDue) * 100)
      : 0;

  const activeTenants = (tenants || []).filter(
    (t) => t.leaseStatus === "Active"
  ).length;
  const occupiedUnits = (units || []).filter((unit) =>
    (tenants || []).some(
      (tenant) => tenant.unitId === unit.id && tenant.leaseStatus === "Active"
    )
  ).length;

  const estimatedExpenses = Math.round(totalRentCollected * 0.15);
  const netIncome = totalRentCollected - estimatedExpenses;

  return (
    <StyledModal
      title={
        <ModalHeader>
          <BankOutlined
            style={{
              color: "white",
              marginRight: "8px",
              filter: "brightness(0) invert(1)",
            }}
          />
          Owner Financial Statements
        </ModalHeader>
      }
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width="90%"
      centered
      maskClosable={!isGenerating}
      keyboard={!isGenerating}
    >
      <ModalContent>
        {/* Date Range Selection */}
        <DateRangeSection>
          <Title level={4}>Statement Period</Title>
          <DateRangeControls>
            <DropdownGroup>
              <DateSelect
                value={selectedDateRange[0].month()}
                style={{ width: 140 }}
                dropdownMatchSelectWidth={false}
                onChange={(month) => {
                  const newStart = selectedDateRange[0]
                    .clone()
                    .month(month)
                    .startOf("month");
                  setSelectedDateRange([newStart, selectedDateRange[1]]);
                }}
              >
                {moment.months().map((monthName, index) => (
                  <Option key={`start-month-${index}`} value={index}>
                    {monthName}
                  </Option>
                ))}
              </DateSelect>
              <DateSelect
                value={selectedDateRange[0].year()}
                style={{ width: 100 }}
                showSearch
                dropdownMatchSelectWidth={false}
                filterOption={(input, option) =>
                  option?.children
                    ?.toString()
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <DropdownInputWrapper>
                      <Input
                        placeholder="Enter year"
                        type="number"
                        onPressEnter={(e) => {
                          const year = parseInt(e.currentTarget.value, 10);
                          if (year >= 1900 && year <= 2100) {
                            const newStart = selectedDateRange[0]
                              .clone()
                              .year(year)
                              .startOf("month");
                            setSelectedDateRange([
                              newStart,
                              selectedDateRange[1],
                            ]);
                          }
                          e.currentTarget.blur();
                          e.currentTarget.value = "";
                        }}
                      />
                    </DropdownInputWrapper>
                  </>
                )}
                onChange={(year) => {
                  const newStart = selectedDateRange[0]
                    .clone()
                    .year(year)
                    .startOf("month");
                  setSelectedDateRange([newStart, selectedDateRange[1]]);
                }}
              >
                {yearOptions.map((year) => (
                  <Option key={`start-year-${year}`} value={year}>
                    {year}
                  </Option>
                ))}
              </DateSelect>
            </DropdownGroup>

            <span style={{ color: "#64748b", fontWeight: 500 }}>to</span>

            <DropdownGroup>
              <DateSelect
                value={selectedDateRange[1].month()}
                style={{ width: 140 }}
                dropdownMatchSelectWidth={false}
                onChange={(month) => {
                  const newEnd = selectedDateRange[1]
                    .clone()
                    .month(month)
                    .endOf("month");
                  setSelectedDateRange([selectedDateRange[0], newEnd]);
                }}
              >
                {moment.months().map((monthName, index) => (
                  <Option key={`end-month-${index}`} value={index}>
                    {monthName}
                  </Option>
                ))}
              </DateSelect>
              <DateSelect
                value={selectedDateRange[1].year()}
                style={{ width: 100 }}
                showSearch
                dropdownMatchSelectWidth={false}
                filterOption={(input, option) =>
                  option?.children
                    ?.toString()
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <DropdownInputWrapper>
                      <Input
                        placeholder="Enter year"
                        type="number"
                        onPressEnter={(e) => {
                          const year = parseInt(e.currentTarget.value, 10);
                          if (year >= 1900 && year <= 2100) {
                            const newEnd = selectedDateRange[1]
                              .clone()
                              .year(year)
                              .endOf("month");
                            setSelectedDateRange([
                              selectedDateRange[0],
                              newEnd,
                            ]);
                          }
                          e.currentTarget.blur();
                          e.currentTarget.value = "";
                        }}
                      />
                    </DropdownInputWrapper>
                  </>
                )}
                onChange={(year) => {
                  const newEnd = selectedDateRange[1]
                    .clone()
                    .year(year)
                    .endOf("month");
                  setSelectedDateRange([selectedDateRange[0], newEnd]);
                }}
              >
                {yearOptions.map((year) => (
                  <Option key={`end-year-${year}`} value={year}>
                    {year}
                  </Option>
                ))}
              </DateSelect>
            </DropdownGroup>
          </DateRangeControls>
          <Text
            type="secondary"
            style={{ marginTop: "12px", display: "block" }}
          >
            Select the period for your financial statement
          </Text>
        </DateRangeSection>

        <Divider />

        {/* Summary Statistics */}
        <SummarySection>
          <SummaryGrid>
            <SummaryCard>
              <SummaryIcon $color="#3f8600">
                <DollarOutlined />
              </SummaryIcon>
              <SummaryContent>
                <SummaryLabel>Rent Collected</SummaryLabel>
                <SummaryValue $color="#3f8600">
                  ${totalRentCollected.toLocaleString()}
                </SummaryValue>
              </SummaryContent>
            </SummaryCard>
            <SummaryCard>
              <SummaryIcon $color="#3f8600">
                <CheckOutlined />
              </SummaryIcon>
              <SummaryContent>
                <SummaryLabel>Collection Rate</SummaryLabel>
                <SummaryValue $color="#3f8600">{collectionRate}%</SummaryValue>
              </SummaryContent>
            </SummaryCard>
            <SummaryCard>
              <SummaryIcon $color="#3f8600">
                <RiseOutlined />
              </SummaryIcon>
              <SummaryContent>
                <SummaryLabel>Net Income</SummaryLabel>
                <SummaryValue $color="#3f8600">
                  ${netIncome.toLocaleString()}
                </SummaryValue>
              </SummaryContent>
            </SummaryCard>
          </SummaryGrid>
        </SummarySection>

        <Divider />

        {/* Action Items */}
        <ActionSection>
          <Title level={4}>Statement Actions</Title>
          <ActionGrid>
            <ActionCard>
              <ActionIcon>
                <FileTextOutlined />
              </ActionIcon>
              <ActionContent>
                <ActionText>
                  <ActionTitle>Generate Statement</ActionTitle>
                  <ActionDescription>
                    Create a financial statement for the selected period
                  </ActionDescription>
                </ActionText>
                <ActionButton
                  type="primary"
                  onClick={handleGenerateStatement}
                  loading={isGenerating}
                  disabled={isGenerating}
                >
                  {isGenerating ? "Generating..." : "Generate Statement"}
                </ActionButton>
              </ActionContent>
            </ActionCard>
          </ActionGrid>
        </ActionSection>

        <Divider />

        {/* Financial Summary */}
        <FinancialSection>
          <Title level={4}>Financial Summary</Title>
          <FinancialGrid>
            <FinancialStatCard>
              <FinancialLabel>Total Rent Due</FinancialLabel>
              <FinancialValue>${totalRentDue.toLocaleString()}</FinancialValue>
            </FinancialStatCard>
            <FinancialStatCard>
              <Tooltip
                title="Estimated expenses include property maintenance, repairs, utilities, insurance, property management fees, and other operational costs. This is calculated as approximately 15% of total rent collected."
                placement="top"
                trigger="hover"
                mouseEnterDelay={0.1}
                destroyTooltipOnHide={false}
              >
                <FinancialLabel
                  style={{ cursor: "help", pointerEvents: "auto" }}
                >
                  Estimated Expenses
                </FinancialLabel>
              </Tooltip>
              <FinancialValue>
                -${estimatedExpenses.toLocaleString()}
              </FinancialValue>
            </FinancialStatCard>
            <FinancialStatCard>
              <FinancialLabel>Active Tenants</FinancialLabel>
              <FinancialValue>{activeTenants}</FinancialValue>
            </FinancialStatCard>
            <FinancialStatCard>
              <FinancialLabel>Occupied Doors</FinancialLabel>
              <FinancialValue>
                {occupiedUnits}/{(units || []).length}
              </FinancialValue>
            </FinancialStatCard>
          </FinancialGrid>
        </FinancialSection>

        <Divider />

        {/* Recent Payments */}
        <PaymentsSection>
          <Title level={4}>Recent Payments</Title>
          {currentPeriodPayments.length === 0 ? (
            <EmptyState>
              <Text type="secondary">
                No payments found for the selected period.
              </Text>
            </EmptyState>
          ) : (
            <List
              dataSource={currentPeriodPayments.slice(0, 5)}
              renderItem={(payment) => {
                const tenant = (tenants || []).find(
                  (t) => t.id === payment.tenantId
                );
                const unit = (units || []).find((u) => u.id === payment.unitId);

                return (
                  <PaymentItem>
                    <PaymentInfo>
                      <PaymentHeader>
                        <PaymentAmount>
                          ${payment.amount?.toLocaleString() || 0}
                        </PaymentAmount>
                        <Tag
                          color={getStatusColor(payment.status)}
                          style={{
                            fontWeight: 600,
                            borderRadius: "6px",
                            fontSize: "12px",
                          }}
                        >
                          {payment.status || "Unknown"}
                        </Tag>
                      </PaymentHeader>
                      <PaymentDetails>
                        <PaymentDetail>
                          <UserOutlined />
                          <Text>
                            {tenant
                              ? `${tenant.firstName || ""} ${
                                  tenant.lastName || ""
                                }`.trim() || tenant.name
                              : "Unknown Tenant"}
                          </Text>
                        </PaymentDetail>
                        <PaymentDetail>
                          <HomeOutlined />
                          <Text>
                            {unit
                              ? unit.name || getUnitDisplayAddress(unit)
                              : "Unknown Door"}
                          </Text>
                        </PaymentDetail>
                        <PaymentDetail>
                          <CalendarOutlined />
                          <Text>
                            {payment.paidDate
                              ?.toDate?.()
                              ?.toLocaleDateString() ||
                              payment.createdAt
                                ?.toDate?.()
                                ?.toLocaleDateString() ||
                              "Unknown"}
                          </Text>
                        </PaymentDetail>
                      </PaymentDetails>
                    </PaymentInfo>
                  </PaymentItem>
                );
              }}
            />
          )}
        </PaymentsSection>
      </ModalContent>
    </StyledModal>
  );
};

// Styled Components
const StyledModal = styled(Modal)`
  --owner-header-height: 56px;
  --owner-close-size: 32px;
  --owner-close-top: 12px;

  .ant-modal-header {
    background: linear-gradient(135deg, var(--color-primary) 0%, #4a7c59 100%);
    border-bottom: none;
    border-radius: 12px 12px 0 0;
    padding: 16px 20px;
    min-height: var(--owner-header-height);
    box-sizing: border-box;
    display: flex;
    align-items: center;
    position: relative;
  }

  .ant-modal-title {
    color: white !important;
    font-weight: 600;
    font-size: 18px;
    line-height: 1.2;
  }

  .ant-modal-close {
    color: white !important;
    font-size: 16px;
    top: var(--owner-close-top) !important;
    right: 20px;
    width: var(--owner-close-size) !important;
    height: var(--owner-close-size) !important;
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;

    .anticon,
    .anticon svg,
    svg {
      color: white !important;
      fill: white !important;
    }

    &:hover {
      color: rgba(255, 255, 255, 0.8) !important;
    }

    &:hover .anticon,
    &:hover .anticon svg,
    &:hover svg {
      color: rgba(255, 255, 255, 0.8) !important;
      fill: rgba(255, 255, 255, 0.8) !important;
    }
  }

  .ant-modal-body {
    padding: 24px;
    background: #fafafa;
    overflow-x: hidden;
  }

  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    max-width: 100vw;
    padding: 0px;
  }

  .ant-modal {
    margin: 20px auto;
  }

  /* Desktop/Tablet: 25% less width */
  @media (min-width: 769px) {
    .ant-modal-wrap .ant-modal {
      width: 67.5% !important;
      max-width: 67.5vw !important;
    }
    .ant-modal-content {
      width: 52rem !important;
      max-width: 100% !important;
      margin: auto;
    }
  }

  /* Mobile optimizations */
  @media (max-width: 768px) {
    --owner-header-height: 48px;
    --owner-close-size: 28px;
    --owner-close-top: 10px;

    .ant-modal {
      max-width: 95vw;
      margin: 10px auto;
    }

    .ant-modal-body {
      padding: 16px;
    }

    .ant-modal-header {
      padding: 12px 16px;
    }

    .ant-modal-title {
      font-size: 16px;
    }

    .ant-modal-close {
      right: 16px !important;
      font-size: 14px;
    }
  }

  @media (max-width: 480px) {
    --owner-header-height: 44px;
    --owner-close-size: 26px;
    --owner-close-top: 9px;

    .ant-modal {
      max-width: 98vw;
      margin: 5px auto;
    }

    .ant-modal-body {
      padding: 12px;
    }

    .ant-modal-header {
      padding: 10px 12px;
    }

    .ant-modal-title {
      font-size: 15px;
    }

    .ant-modal-close {
      right: 12px !important;
      font-size: 12px;
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 600;
  color: white;
`;

const ModalContent = styled.div`
  max-height: 70vh;
  overflow-y: auto;
  overflow-x: hidden;
  background: white;
  border-radius: 12px;
  padding: 20px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px;
    max-height: 75vh;
  }

  @media (max-width: 480px) {
    padding: 12px;
    max-height: 80vh;
  }
`;

const DateRangeSection = styled.div`
  margin-bottom: 24px;
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  h4 {
    font-size: 18px;
    font-weight: 600;
    color: var(--color-text);
    margin-bottom: 16px;
  }

  /* Fix vertical alignment of Select dropdown arrows */
  .ant-select {
    display: flex;
    align-items: center;
  }

  .ant-select-selector {
    display: flex !important;
    align-items: center !important;
    height: 32px !important;
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    display: flex !important;
    align-items: center !important;
    line-height: 1 !important;
  }

  .ant-select-arrow {
    position: absolute !important;
    top: 50% !important;
    right: 11px !important;
    transform: translateY(-50%) !important;
    margin-top: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  .ant-select-arrow .anticon {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  @media (max-width: 768px) {
    margin-bottom: 20px;
    padding: 16px;

    h4 {
      font-size: 16px;
      margin-bottom: 12px;
    }
  }

  @media (max-width: 480px) {
    margin-bottom: 16px;
    padding: 14px;

    h4 {
      font-size: 15px;
      margin-bottom: 10px;
    }
  }
`;

const DateRangeControls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const DropdownGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const DropdownInputWrapper = styled.div`
  padding: 8px 12px;
  width: 150px;
`;

const DateSelect = styled(Select)`
  .ant-select-selector {
    height: 38px !important;
    border-radius: 8px !important;
    display: flex !important;
    align-items: center !important;
    padding: 0 12px !important;
    position: relative !important;
  }

  .ant-select-selection-item {
    display: flex !important;
    align-items: center !important;
    line-height: 1 !important;
  }

  .ant-select-arrow {
    position: absolute !important;
    top: 50% !important;
    right: 11px !important;
    transform: translateY(-50%) !important;
    margin-top: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    height: auto !important;
  }

  .ant-select-arrow .anticon {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    vertical-align: middle !important;
  }
`;

const SummarySection = styled.div`
  margin-bottom: 24px;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }

  @media (max-width: 480px) {
    margin-bottom: 16px;
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #e8e8e8;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--color-primary);
    box-shadow: 0 4px 12px rgba(44, 95, 45, 0.15);
  }

  @media (max-width: 768px) {
    padding: 16px;
    gap: 12px;
  }

  @media (max-width: 480px) {
    padding: 14px;
    gap: 10px;
  }
`;

const SummaryIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${(props) => {
    if (props.$color === "#3f8600") return "rgba(63, 134, 0, 0.1)";
    return "rgba(44, 95, 45, 0.1)";
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  .anticon {
    font-size: 24px;
    color: ${(props) => props.$color || "#2c5f2d"};
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;

    .anticon {
      font-size: 20px;
    }
  }

  @media (max-width: 480px) {
    width: 36px;
    height: 36px;

    .anticon {
      font-size: 18px;
    }
  }
`;

const SummaryContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SummaryLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-light);
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    font-size: 12px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
  }
`;

const SummaryValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${(props) => props.$color || "#2c5f2d"};
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 20px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

const ActionSection = styled.div`
  margin-bottom: 24px;

  h4 {
    font-size: 18px;
    font-weight: 600;
    color: var(--color-text);
    margin-bottom: 16px;
  }

  @media (max-width: 768px) {
    margin-bottom: 20px;

    h4 {
      font-size: 16px;
      margin-bottom: 12px;
    }
  }

  @media (max-width: 480px) {
    margin-bottom: 16px;

    h4 {
      font-size: 15px;
      margin-bottom: 10px;
    }
  }
`;

const ActionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px;
  margin-top: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
    margin-top: 12px;
  }

  @media (max-width: 480px) {
    gap: 10px;
    margin-top: 10px;
  }
`;

const ActionCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--color-primary);
    box-shadow: 0 4px 12px rgba(44, 95, 45, 0.15);
  }

  @media (max-width: 768px) {
    padding: 14px;
    gap: 10px;
  }

  @media (max-width: 480px) {
    padding: 12px;
    gap: 10px;
    flex-direction: column;
    text-align: center;
  }
`;

const ActionIcon = styled.div`
  width: 40px;
  height: 40px;
  color: #1e293b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
  background: none;
  box-shadow: none;

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 18px;
  }

  @media (max-width: 480px) {
    width: 36px;
    height: 36px;
    font-size: 18px;
    margin: 0 auto 6px;
  }
`;

const ActionContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 12px;
    text-align: center;
    align-items: center;
  }
`;

const ActionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;

  @media (max-width: 480px) {
    align-items: center;
  }
`;

const ActionTitle = styled.div`
  font-weight: 600;
  font-size: 15px;
  color: var(--color-text);
  margin-bottom: 4px;

  @media (max-width: 768px) {
    font-size: 14px;
  }

  @media (max-width: 480px) {
    font-size: 13px;
    margin-bottom: 3px;
  }
`;

const ActionDescription = styled.div`
  font-size: 13px;
  color: var(--color-text-light);
  margin-bottom: 10px;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 12px;
    margin-bottom: 8px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
    margin-bottom: 6px;
  }
`;

const ActionButton = styled(Button)`
  margin-top: 0;
  margin-left: auto;
  border-radius: 6px;
  font-weight: 600;
  height: 32px;
  padding: 0 12px;
  font-size: 13px;

  &.ant-btn-primary {
    background: var(--color-primary);
    border-color: var(--color-primary);

    &:hover {
      background: #4a7c59;
      border-color: #4a7c59;
    }
  }

  &.ant-btn-default {
    border-color: #d9d9d9;
    color: var(--color-text);

    &:hover {
      border-color: var(--color-primary);
      color: var(--color-primary);
    }
  }

  @media (max-width: 768px) {
    margin-left: 0;
    margin-top: 6px;
    height: 30px;
    padding: 0 10px;
    font-size: 12px;
  }

  @media (max-width: 480px) {
    height: 28px;
    padding: 0 8px;
    font-size: 11px;
    width: 100%;
    margin-top: 4px;
  }
`;

const FinancialSection = styled.div`
  margin-bottom: 24px;

  h4 {
    font-size: 18px;
    font-weight: 600;
    color: var(--color-text);
    margin-bottom: 16px;
  }

  @media (max-width: 768px) {
    margin-bottom: 20px;

    h4 {
      font-size: 16px;
      margin-bottom: 12px;
    }
  }

  @media (max-width: 480px) {
    margin-bottom: 16px;

    h4 {
      font-size: 15px;
      margin-bottom: 10px;
    }
  }
`;

const FinancialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const FinancialStatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--color-primary);
    box-shadow: 0 4px 12px rgba(44, 95, 45, 0.15);
  }

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 480px) {
    padding: 14px;
  }
`;

const FinancialLabel = styled.div`
  font-size: 13px;
  color: var(--color-text-light);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    font-size: 12px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
  }
`;

const FinancialValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: var(--color-primary);
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 20px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

const PaymentsSection = styled.div`
  margin-bottom: 24px;

  h4 {
    font-size: 18px;
    font-weight: 600;
    color: var(--color-text);
    margin-bottom: 16px;
  }

  @media (max-width: 768px) {
    margin-bottom: 20px;

    h4 {
      font-size: 16px;
      margin-bottom: 12px;
    }
  }

  @media (max-width: 480px) {
    margin-bottom: 16px;

    h4 {
      font-size: 15px;
      margin-bottom: 10px;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #999;

  @media (max-width: 768px) {
    padding: 30px 15px;
  }

  @media (max-width: 480px) {
    padding: 20px 10px;
    font-size: 14px;
  }
`;

const PaymentItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  margin-bottom: 12px;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--color-primary);
    box-shadow: 0 4px 12px rgba(44, 95, 45, 0.15);
  }

  @media (max-width: 768px) {
    padding: 14px;
    margin-bottom: 10px;
  }

  @media (max-width: 480px) {
    padding: 12px;
    margin-bottom: 8px;
    flex-direction: column;
    gap: 10px;
  }
`;

const PaymentInfo = styled.div`
  flex: 1;

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const PaymentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    gap: 8px;
    margin-bottom: 8px;
  }

  @media (max-width: 480px) {
    gap: 6px;
    margin-bottom: 6px;
    flex-wrap: wrap;
  }
`;

const PaymentAmount = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #2c5f2d;

  @media (max-width: 768px) {
    font-size: 16px;
  }

  @media (max-width: 480px) {
    font-size: 15px;
  }
`;

const PaymentDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px 24px;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const PaymentDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex: 0 1 auto;
  font-size: 13px;
  color: var(--color-text-light);

  .anticon {
    color: var(--color-text-light);
    font-size: 13px;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    font-size: 12px;

    .anticon {
      font-size: 12px;
    }
  }

  @media (max-width: 480px) {
    font-size: 11px;

    .anticon {
      font-size: 11px;
    }
  }
`;

export default OwnerStatementsModal;
